import { useEffect, useMemo, useState } from "react";
import { loadArtistSettings, saveArtistThemeSetting } from "../api/localArtistPersistence";

const VALID_MODES = new Set(["dark", "light"]);
const DEFAULT_MODE = "light";

export function readGuestThemeMode() {
    const stored = localStorage.getItem("app_theme_mode");
    return VALID_MODES.has(stored) ? stored : DEFAULT_MODE;
}

function readArtistThemeMode(artistId) {
    if (!artistId) {
        return null;
    }
    const { themeSettings } = loadArtistSettings(artistId);
    const mode = themeSettings?.mode;
    return VALID_MODES.has(mode) ? mode : null;
}

function readPersistedThemeMode(artistId) {
    return readArtistThemeMode(artistId) ?? readGuestThemeMode();
}

function persistThemeMode(artistId, mode) {
    if (artistId) {
        saveArtistThemeSetting(artistId, { mode });
    } else {
        localStorage.setItem("app_theme_mode", mode);
    }
}

export function useAppThemeMode(loggedInArtistID) {
    const scopeKey = loggedInArtistID ?? "guest";

    const [modeState, setModeState] = useState(() => ({
        scopeKey,
        mode: readPersistedThemeMode(loggedInArtistID),
    }));

    if (modeState.scopeKey !== scopeKey) {
        setModeState({
            scopeKey,
            mode: readPersistedThemeMode(loggedInArtistID),
        });
    }

    useEffect(() => {
        if (!loggedInArtistID) {
            return;
        }
        const { themeSettings } = loadArtistSettings(loggedInArtistID);
        if (!themeSettings) {
            persistThemeMode(loggedInArtistID, readPersistedThemeMode(loggedInArtistID));
        }
    }, [loggedInArtistID, modeState.scopeKey]);

    const colorMode = useMemo(
        () => ({
            saveColorMode: () => persistThemeMode(loggedInArtistID, modeState.mode),
            toggleColorMode: () => {
                setModeState((prev) => {
                    const newMode = prev.mode === "light" ? "dark" : "light";
                    persistThemeMode(loggedInArtistID, newMode);
                    return { ...prev, mode: newMode };
                });
            },
            setColorMode: (mode) => {
                setModeState((prev) => ({ ...prev, mode }));
                persistThemeMode(loggedInArtistID, mode);
            },
        }),
        [loggedInArtistID, modeState.mode]
    );

    return { mode: modeState.mode, colorMode };
}
